import { useActions } from '../../hooks';
import ContactModal from './ContactModal';
import { formatDate } from '../../utilities';
import { useAppContext } from '../../shared';
import { useContacts } from '../../templates';
import { ContactName } from '../../templates';
import { Slug, Button } from '../../components';
import { ContactsListTemplate } from '../../templates';
import { useContactActions } from './useContactActions';
import { getContactStateColor as getColor } from '../../utilities';
import { addContact, updateContact, deleteContact } from '../../api';
import type { ContactItemType } from '../../templates';

const Contacts = () => {
  const { meta } = useAppContext();

  const listResult = useContacts();
  const { refresh } = listResult;

  const {
    element,
    isOpened,
    updating,
    initialValues,
    handleClose,
    handleStartAdd,
    handleAdd,
    handleEdit,
    handleStartEdit,
    handleStartDelete,
  } = useActions<ContactItemType, ContactItemType, ContactItemType>({
    name: 'Contact',
    nameKey: 'email',

    onAdd: addContact,
    onEdit: updateContact,
    onDelete: deleteContact,
    onRefresh: refresh,
    deleteConfig: {
      getText: item => {
        return `The user ${item.email} and all related data will be deleted.\nAre you sure?`;
      },
    },
  });

  const { startElement, openStart, wipeElement, openWipe } =
    useContactActions(refresh);

  return (
    <>
      {element}
      {startElement}
      {wipeElement}
      {isOpened && (
        <ContactModal
          loading={updating}
          initialValues={initialValues}
          onClose={handleClose}
          onSumbit={initialValues ? handleEdit : handleAdd}
        />
      )}
      <ContactsListTemplate
        listResult={listResult}
        buttons={
          <Button icon="plus" onClick={() => handleStartAdd()}>
            Add Contact
          </Button>
        }
        columns={[
          {
            header: 'Contact',
            sortName: 'email',
            cellClassName: 'text-small',
            getValue: contact => (
              <ContactName contact={contact} withMessagesLink />
            ),
          },
          {
            header: 'Campaign',
            getValue: ({ campaign_id: id }) => {
              const campaign = meta.campaigns.find(v => v.value === id);

              return <Slug text={campaign?.label || id} />;
            },
          },
          {
            header: 'State',
            sortName: 'latest_contact_state',
            cellClassName: 'max-w-[15rem]',
            getValue: ({
              latest_contact_state,
              latest_contact_state_label,
            }) => (
              <Slug
                className="max-w-[13rem]"
                color={getColor(latest_contact_state)}
                text={latest_contact_state_label || 'No State'}
              />
            ),
          },
          {
            header: 'Description',
            sortName: 'description',
            cellClassName: 'text-tiny',
            getValue: ({ description }) => description,
          },
          {
            header: 'Created',
            sortName: 'created_at',
            cellClassName: 'text-tiny',
            getValue: ({ created_at }) => formatDate(created_at),
          },
          {
            header: 'Actions',
            align: 'right',
            getValue: item => (
              <div className="flex flex-row gap-x-sm justify-end">
                <Button
                  icon="play"
                  type="primary"
                  tooltip="Start Campaign"
                  onClick={() => openStart(item)}
                  disabled={!!item.latest_contact_state}
                />
                <Button
                  icon="edit"
                  tooltip="Edit Contact"
                  onClick={() => handleStartEdit(item)}
                />
                <Button
                  icon="eraser"
                  tooltip="Clear Contact Data"
                  onClick={() => openWipe(item)}
                />
                <Button
                  icon="delete"
                  type="danger"
                  tooltip="Delete Contact"
                  onClick={() => handleStartDelete(item)}
                />
              </div>
            ),
          },
        ]}
      />
    </>
  );
};

export default Contacts;
